import { createFileRoute } from "@tanstack/react-router";
import { DriverLandingPage, type DriverLandingContent } from "@/components/DriverLandingPage";
import { MOTPrepChecklistBlock } from "@/components/MOTPrepChecklistBlock";
import { seoHead, localBusinessSchema, complianceToBusinessOpts, serviceSchema } from "@/lib/seo";
import { faqSchema } from "@/components/FAQAccordion";
import { getPublicConfig } from "@/lib/site-content.functions";

const CONTENT: DriverLandingContent = {
  eyebrow: "PCO & Private-Hire Driver Support",
  h1: "MOT & Garage Support for PCO and Private-Hire Drivers",
  intro:
    "Keep your work vehicle roadworthy with clear MOT, servicing, diagnostics and repair support from Woodseer Autos in East London. We help private-hire and PCO drivers stay on the road — call to confirm what inspection or paperwork you need.",
  trustChips: [
    "DVSA-authorised MOT station",
    "VTS V106376",
    "MOT classes 1, 2, 3, 4, 5 & 7",
    "Fast communication",
    "Value-for-money repairs",
  ],
  audienceTitle: "Who this page is for",
  audience: [
    "Uber drivers",
    "Bolt drivers",
    "Private-hire and minicab drivers",
    "Taxi drivers",
    "Delivery drivers and couriers",
    "High-mileage commuters",
    "Local fleet and work-van operators",
  ],
  helpTitle: "What we can help with",
  helpItems: [
    "MOT testing (classes 1, 2, 3, 4, 5 and 7)",
    "Pre-MOT checks and advisories",
    "Interim, full and major servicing",
    "Warning-light diagnostics",
    "Brakes, pads and discs",
    "Tyres, fitting and alignment",
    "Suspension, bushes and links",
    "Bodywork, scratches and scuffs",
    "Urgent repair advice for working vehicles",
  ],
  issuesTitle: "Common issues for high-mileage drivers",
  issues: [
    "Tyres wearing quickly on the front axle",
    "Brake pads needing replacement every few months",
    "Suspension knocks and uneven wear",
    "Engine management and ABS warning lights",
    "Service intervals creeping past on busy weeks",
    "Emissions and DPF warnings on diesels",
    "Bodywork scuffs from car parks and tight streets",
    "Dashboard warnings before a booked job",
  ],
  valueTitle: "Value-for-money garage support",
  valueBody:
    "We aim to offer clear, fair and practical advice before work starts, so drivers can make informed decisions. We quote in writing, explain urgent vs advisory repairs, and never carry out work without your approval — important when every day off the road costs you money.",
  beforeTitle: "Before you arrive",
  beforeItems: [
    "Bring the vehicle's registration document",
    "Bring any private-hire paperwork relevant to the job",
    "Know your MOT or licensing deadline",
    "Note any warning lights and when they appeared",
    "Bring the locking wheel nut key if you have one",
    "Call ahead for urgent or same-day slots",
  ],
  faqs: [
    { q: "Do you help PCO and private-hire drivers?", a: "Yes — we regularly look after private-hire, Uber, Bolt and minicab drivers for MOT testing, servicing, diagnostics and repairs. For official TfL PCO licensing inspections call us first so we can confirm exactly what you need." },
    { q: "Can I book an MOT for a high-mileage vehicle?", a: "Yes. We test classes 1, 2, 3, 4, 5 and 7 at our DVSA-authorised station V106376. We're used to high-mileage work vehicles — call us with the reg and we'll find a slot." },
    { q: "Can you check warning lights before an MOT?", a: "Yes — a pre-MOT diagnostic is usually cheaper than failing the test and paying for a retest. We'll scan the car and tell you what's actually worth fixing first." },
    { q: "Can you help with brakes, tyres and suspension?", a: "All standard work for us. Pads, discs, calipers, tyre fitting, alignment, springs, bushes and shock absorbers — quoted before any work starts." },
    { q: "Can you do same-day repairs?", a: "Often, yes — depending on parts availability and workshop load. Always call first so we can prioritise the slot for a working driver." },
    { q: "Should I call before coming in?", a: "Yes please. We're a busy workshop and a quick call lets us reserve a bay, confirm parts and give you a realistic time for collection." },
  ],
  bookingContext: "pco-private-hire",
  waPrefill: "I'm enquiring about MOT/garage support for a PCO/private-hire or high-mileage vehicle. Reg: ",
  relatedLinks: [
    { to: "/mot", label: "MOT testing" },
    { to: "/servicing", label: "Servicing" },
    { to: "/diagnostics", label: "Diagnostics" },
    { to: "/mechanical-repairs", label: "Mechanical repairs" },
    { to: "/cost/mot-east-london", label: "MOT cost guide" },
    { to: "/mot-checklist-before-test", label: "Pre-MOT checklist" },
    { to: "/garage-for-uber-bolt-drivers-east-london", label: "Uber & Bolt driver garage" },
    { to: "/value-for-money-garage-east-london", label: "Value-for-money garage" },
  ],
};

export const Route = createFileRoute("/pco-mot-east-london")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/pco-mot-east-london",
      title: "PCO MOT & Private Hire Car MOT East London | Woodseer Autos",
      description:
        "MOT testing, servicing, diagnostics and repairs for private-hire and PCO drivers in East London. Value-for-money garage support in E14. Call Woodseer Autos.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "PCO / Private Hire MOT", path: "/pco-mot-east-london" },
      ],
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
        serviceSchema({
          name: "MOT & Garage Support for PCO and Private-Hire Drivers",
          serviceType: "Automotive Repair & MOT Testing",
          description:
            "MOT testing, servicing, diagnostics and repairs for private-hire and PCO drivers in East London. DVSA-authorised station V106376.",
          areaServed: "East London, E14",
          url: "https://woodseerautos.co.uk/pco-mot-east-london",
        }),
        faqSchema(CONTENT.faqs) as Record<string, unknown>,
      ],
    }),
  component: () => (
    <>
      <DriverLandingPage content={CONTENT} path="/pco-mot-east-london" />
      <MOTPrepChecklistBlock />
    </>
  ),
});
